import type { DocumentVisual } from "../studio/visual-design.js";
import type { ClientId, EquipmentId, ProfessionalId } from "../../shared/kernel/identifiers.js";
import type { Api570RateBasis, Api570Result } from "./api570-calculator.js";
import { determineAvrCriticality, type AvrCriticality } from "./avr-domain.js";

/**
 * AVR — Avaliação de Vida Remanescente.
 *
 * Toda espessura usada aqui tem origem rastreável num Relatório ME ou B-Scan
 * do INSPECT IA: a leitura carrega relatório, revisão, data e ponto/região.
 * Nada é digitado no AVR. A taxa de corrosão e a vida remanescente NÃO são
 * calculadas neste módulo: vêm do motor API 570 já existente
 * (api570-calculator.ts via EngineeringCalculationService) e ficam gravadas
 * aqui como resultado, com a origem dos dados que o alimentaram.
 */

export type AvrSourceKind = "ME" | "BSCAN";

export const AVR_SOURCE_KIND_LABELS: Record<AvrSourceKind, string> = { ME: "ME", BSCAN: "B-Scan" };

/** Um relatório de origem disponível/selecionado — sempre um relatório real do sistema. */
export interface AvrSourceReport {
  kind: AvrSourceKind;
  reportId: string;
  reportNumber: string;
  revision: string;
  /** "aaaa-mm-dd" — data do ensaio. */
  testDate: string;
  /** Status do workflow do relatório de origem, como ele o declara. */
  status: string;
  /** VALIDATED/SIGNED (ou equivalente): fonte aprovada para uma avaliação definitiva. */
  approved: boolean;
  equipmentId: string;
  equipmentTag: string;
  /** OS / Sub-OS do relatório de origem, quando ele as registra — a AVR herda se ainda não tiver as suas. */
  osWorkOrderId: string | null;
  osNumber: string;
  osSubWorkOrderId: string | null;
  subOsNumber: string;
  /** O relatório de origem tem croqui que a AVR pode reaproveitar. */
  hasCroqui: boolean;
}

/** Uma leitura de espessura, com a sua origem completa. */
export interface AvrThicknessReading {
  sourceKind: AvrSourceKind;
  reportId: string;
  reportNumber: string;
  revision: string;
  /** "aaaa-mm-dd" */
  date: string;
  /** ME: ID DO PONTO. B-Scan: região/trecho. */
  pointId: string;
  component: string;
  location: string;
  nominalMm: number | null;
  minMm: number | null;
  /**
   * A ESPESSURA QUE VALE PARA O CÁLCULO.
   *
   * Em medição de alta temperatura é a Ea (Espessura Aproximada, MÉTODO A —
   * RESULTADO APROXIMADO), não a leitura feita quente: taxa de corrosão e
   * vida remanescente precisam da espessura aproximada do componente. A
   * leitura original fica em `hotMeasuredThicknessMm`, para rastreabilidade.
   */
  thicknessMm: number;
  /** Emq, quando a leitura foi feita acima de 60 °C. Null no fluxo convencional. */
  hotMeasuredThicknessMm?: number | null;
  /** TME da medição, quando houve alta temperatura. */
  temperatureC?: number | null;
  /** Rastreabilidade MÉTODO A: FC_A (precisão total) e o Procedimento de onde vieram Vsa/k/Temperatura de Referência. */
  correctionFactor?: number | null;
  procedureCode?: string | null;
  /** Chave interna do ponto no relatório de origem (a medição). */
  measurementKey?: string | null;
  /** Emq — Espessura Medida a Quente, para auditoria (mesmo que hotMeasuredThicknessMm). */
  ematMm?: number | null;
  /** Código do procedimento/calibração usado (no Método A é o procedureCode). */
  calibrationId?: string | null;
  approved: boolean;
}

/**
 * Chave de correlação entre relatórios (item 11): o mesmo ponto só é o mesmo
 * quando ID, componente e localização coincidem — "P01" de dois relatórios
 * com componentes diferentes são dois pontos.
 */
export function correlationKey(reading: Pick<AvrThicknessReading, "sourceKind" | "pointId" | "component" | "location">): string {
  const norm = (v: string) => (v ?? "").trim().toUpperCase().replace(/\s+/g, " ");
  return `${reading.sourceKind}|${norm(reading.pointId)}|${norm(reading.component)}|${norm(reading.location)}`;
}

export const AVR_CRITICALITY_LABELS: Record<AvrCriticality, string> = {
  LOW: "BAIXA",
  MODERATE: "MODERADA",
  HIGH: "ALTA",
  CRITICAL: "CRÍTICA",
  UNCLASSIFIED: "SEM DADOS"
};

export type AvrSituation =
  | "OK"
  | "BELOW_MINIMUM"
  | "INSUFFICIENT_HISTORY"
  | "NO_MINIMUM"
  | "NO_LOSS";

export const AVR_SITUATION_LABELS: Record<AvrSituation, string> = {
  OK: "Calculado",
  BELOW_MINIMUM: "ABAIXO DA ESPESSURA MÍNIMA — AVALIAÇÃO DE ENGENHARIA NECESSÁRIA",
  INSUFFICIENT_HISTORY: "DADOS HISTÓRICOS INSUFICIENTES PARA CÁLCULO DA TAXA DE CORROSÃO",
  NO_MINIMUM: "ESPESSURA MÍNIMA NÃO DEFINIDA",
  NO_LOSS: "SEM PERDA MENSURÁVEL NO PERÍODO"
};

/** Um ponto/região correlacionado ao longo dos relatórios, com o resultado do motor API 570. */
export interface AvrComponentAnalysis {
  key: string;
  sourceKind: AvrSourceKind;
  pointId: string;
  component: string;
  location: string;
  /** Leituras em ordem cronológica — o histórico completo, não só duas. */
  readings: AvrThicknessReading[];
  /** Espessura mínima efetivamente usada e de onde veio. */
  minimumThicknessMm: number | null;
  minimumThicknessOrigin: string;
  /**
   * Referências históricas efetivamente enviadas ao motor: CURTO PRAZO
   * (medição anterior) e LONGO PRAZO (medição inicial), cada uma com a
   * origem — relatório histórico correlacionado ou dado complementar do AVR.
   */
  shortTerm: AvrEffectiveReference | null;
  longTerm: AvrEffectiveReference | null;
  /** Resultado devolvido pelo motor API 570 — nulo enquanto não calculado. */
  api570: Api570Result | null;
  situation: AvrSituation;
  criticality: AvrCriticality;
}

/**
 * Workflow: elaboração (DRAFT/CALCULATED/OUTDATED/VALIDATED) → envio ao PLH
 * (AWAITING_PLH_REVIEW → PLH_REVIEWING) → PLH aprova a análise (PLH_APPROVED)
 * ou solicita correção (CORRECTION_REQUESTED, volta ao Engenheiro) → assinatura
 * (SIGNED). Aprovar a análise e assinar o documento são atos distintos.
 */
export type AvrReportStatus =
  | "DRAFT"
  | "CALCULATED"
  | "OUTDATED"
  | "VALIDATED"
  | "AWAITING_PLH_REVIEW"
  | "PLH_REVIEWING"
  | "CORRECTION_REQUESTED"
  | "PLH_APPROVED"
  | "REJECTED"
  | "SIGNED";

/** Rótulos em português — o enum interno nunca vaza para a interface. */
export const AVR_STATUS_LABELS: Record<AvrReportStatus, string> = {
  DRAFT: "Em Elaboração",
  CALCULATED: "Calculado",
  OUTDATED: "Cálculo Desatualizado",
  VALIDATED: "Validado",
  AWAITING_PLH_REVIEW: "Aguardando Análise do PLH",
  PLH_REVIEWING: "Em Análise do PLH",
  CORRECTION_REQUESTED: "Correção Solicitada pelo PLH",
  PLH_APPROVED: "Análise do PLH Aprovada",
  REJECTED: "Reprovada",
  SIGNED: "Assinado"
};

/** Estados que a Engenharia enxerga como "AVR para análise / acompanhamento" (só a exclusão fica de fora — não há status EXCLUÍDO/CANCELADO: a AVR excluída some do repositório). */
export const AVR_ENGINEERING_STATUSES: readonly AvrReportStatus[] = ["DRAFT", "CALCULATED", "OUTDATED", "VALIDATED", "AWAITING_PLH_REVIEW", "PLH_REVIEWING", "CORRECTION_REQUESTED", "PLH_APPROVED", "REJECTED", "SIGNED"];

/** FINALIZAR RELATÓRIO (AVR): registro distinto da assinatura. */
export interface AvrFinalization {
  finalizedAt: string;
  finalizedByUserId: string;
  finalizedByName: string;
  revision: string;
  /** SHA-256 do modelo documental final. */
  snapshotHash: string;
  /** "FINALIZADO" (já assinada) ou "FINALIZADO_AGUARDANDO_ASSINATURA". */
  state: "FINALIZADO" | "FINALIZADO_AGUARDANDO_ASSINATURA";
}

/** REPROVAR AVR: motivo obrigatório; a AVR não é excluída. */
export interface AvrRejection {
  rejectedAt: string;
  rejectedByUserId: string;
  rejectedByProfessionalId: string | null;
  rejectedByName: string;
  reason: string;
  revision: string;
}

/** Estados em que o Engenheiro pode enviar (ou reenviar) para análise do PLH. */
export const AVR_SUBMITTABLE_STATUSES: readonly AvrReportStatus[] = ["DRAFT", "CALCULATED", "OUTDATED", "VALIDATED", "CORRECTION_REQUESTED"];

/** Estados em que os dados técnicos ficam bloqueados para edição normal. */
export const AVR_LOCKED_STATUSES: readonly AvrReportStatus[] = ["AWAITING_PLH_REVIEW", "PLH_REVIEWING", "PLH_APPROVED", "REJECTED", "SIGNED"];

export function isAvrLocked(status: AvrReportStatus): boolean {
  return AVR_LOCKED_STATUSES.includes(status);
}

export function isAvrSubmittable(status: AvrReportStatus): boolean {
  return AVR_SUBMITTABLE_STATUSES.includes(status);
}

/** Correção pedida pelo PLH — mostrada em destaque na elaboração até o reenvio. */
export interface AvrCorrectionRequest {
  reason: string;
  /** Campos/pontos envolvidos, quando o PLH informa. */
  points: string;
  requestedAt: string;
  requestedByUserId: string;
  requestedByProfessionalId: ProfessionalId | null;
  requestedByName: string;
  /** Primeira alteração feita pelo Engenheiro depois do pedido. */
  correctedAt: string | null;
}

/** Ciclo de análise do PLH — mesmo avrId, mesma revisão; o histórico acumula os ciclos. */
export interface AvrPlhReview {
  /** Quantas vezes esta revisão foi enviada ao PLH (1 = primeiro envio). */
  cycle: number;
  plhProfessionalId: ProfessionalId;
  plhName: string;
  submittedAt: string;
  submittedByUserId: string;
  submittedByProfessionalId: ProfessionalId | null;
  submittedByName: string;
  startedAt: string | null;
  correction: AvrCorrectionRequest | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  approvedByProfessionalId: ProfessionalId | null;
  approvedByName: string;
  approvedRevision: string | null;
  /** SHA-256 do documento inteiro no momento da aprovação — não só um trecho. */
  approvalHash: string | null;
  approvalComment: string;
  /** Documento como foi enviado ao PLH — o que ele analisou fica preservado. */
  snapshotAtSubmission: AvrDocumentModel | null;
}

export type AvrSignatoryRole = "ELABORADO_POR" | "PLH";

export interface AvrSignatory {
  role: AvrSignatoryRole;
  professionalId: ProfessionalId | null;
  name: string;
  functionTitle: string;
  credential: string;
  signatureImageId: string | null;
  signedAt: string | null;
}

export interface AvrHistoryEntry {
  at: string;
  by: string;
  action:
    | "CREATED" | "SAVED" | "SOURCES_CHANGED" | "SOURCES_REFRESHED" | "CALCULATED" | "VALIDATED" | "SIGNED" | "PDF_GENERATED"
    | "SUBMITTED_FOR_PLH_REVIEW" | "RESUBMITTED_FOR_PLH_REVIEW" | "PLH_REVIEW_STARTED" | "PLH_CORRECTION_REQUESTED"
    | "CORRECTION_DONE" | "PLH_RETURNED" | "PLH_REVIEW_APPROVED" | "POINT_HISTORY_CHANGED" | "PLH_REJECTED" | "FINALIZED";
  detail: string;
  /** Transição de status registrada no evento (trilha mínima quando o AuditRecorder real não está ligado). */
  fromStatus?: AvrReportStatus;
  toStatus?: AvrReportStatus;
  professionalId?: string | null;
}

export const AVR_HISTORY_LABELS: Record<AvrHistoryEntry["action"], string> = {
  CREATED: "Criação",
  SAVED: "Rascunho salvo",
  SOURCES_CHANGED: "Fontes alteradas",
  SOURCES_REFRESHED: "Fontes atualizadas",
  CALCULATED: "Cálculo API 570",
  VALIDATED: "Validação",
  SIGNED: "Assinatura PLH",
  PDF_GENERATED: "PDF gerado",
  SUBMITTED_FOR_PLH_REVIEW: "AVR enviada para análise do PLH",
  RESUBMITTED_FOR_PLH_REVIEW: "AVR reenviada para análise do PLH",
  PLH_REVIEW_STARTED: "Análise do PLH iniciada",
  PLH_CORRECTION_REQUESTED: "Correção solicitada pelo PLH",
  CORRECTION_DONE: "Correção realizada pelo Engenheiro",
  PLH_RETURNED: "Devolvida pelo PLH para elaboração",
  PLH_REVIEW_APPROVED: "Análise do PLH aprovada",
  POINT_HISTORY_CHANGED: "Histórico do ponto alterado",
  PLH_REJECTED: "AVR reprovada",
  FINALIZED: "Relatório finalizado"
};

// ---------------------------------------------------------- HISTÓRICO POR PONTO

export type AvrPointHistoryOrigin = "MANUAL" | "SOURCE_REPORT";

export const AVR_POINT_HISTORY_ORIGIN_LABELS: Record<AvrPointHistoryOrigin, string> = {
  MANUAL: "DADO COMPLEMENTAR INFORMADO NO AVR",
  SOURCE_REPORT: "RELATÓRIO HISTÓRICO"
};

export type AvrHistoryTerm = "SHORT" | "LONG";

export const AVR_HISTORY_TERM_LABELS: Record<AvrHistoryTerm, string> = { SHORT: "Curto Prazo", LONG: "Longo Prazo" };

/** Uma referência histórica (espessura + data) com origem e trilha. Valor sempre NUMBER. */
export interface AvrHistoryReference {
  thicknessMm: number;
  /** "aaaa-mm-dd" */
  date: string;
  origin: AvrPointHistoryOrigin;
  /** "ME-2025-0012 / P01" quando confirmada a partir de um relatório; senão o rótulo MANUAL. */
  sourceReference: string;
  updatedAt: string;
  updatedBy: string;
}

/**
 * Histórico complementar de UM ponto: duas referências independentes —
 * CURTO PRAZO (medição anterior) e LONGO PRAZO (medição inicial). Cada
 * ponto é independente; salvar parcialmente é permitido (rascunho). Quando
 * o relatório histórico correlacionado existe, o sistema só SUGERE — o que
 * fica gravado aqui é o que o Engenheiro confirmou.
 */
export interface AvrPointHistory {
  /** correlationKey do ponto. */
  key: string;
  pointId: string;
  shortTerm: AvrHistoryReference | null;
  longTerm: AvrHistoryReference | null;
}

/** Referência efetiva de um termo, como entra no motor (histórico informado ou relatório correlacionado). */
export interface AvrEffectiveReference {
  thicknessMm: number;
  date: string;
  origin: AvrPointHistoryOrigin;
  reference: string;
}

export const AVR_SHORT_THICKNESS_INVALID = "Informe uma espessura de curto prazo válida.";
export const AVR_LONG_THICKNESS_INVALID = "Informe uma espessura de longo prazo válida.";
export const AVR_SHORT_DATE_INVALID = "A data da medição de curto prazo deve ser anterior à data da medição atual.";
export const AVR_LONG_BEFORE_SHORT_INVALID = "A data da medição de longo prazo deve ser anterior à data utilizada para o curto prazo.";
export const AVR_LONG_DATE_INVALID = "A data da medição de longo prazo deve ser anterior à data da medição atual.";

/** "6" → 6, "6,5" → 6.5, "6.55" → 6.55; inválido → null. */
export function parseThicknessInput(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  const text = String(raw).trim().replace(/\s+/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".");
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

/** Padrão 0,00 para os campos de espessura da tela ("6" → "6,00"; "6,2" → "6,20"). */
export function formatThicknessInput(raw: string | number | null | undefined): string {
  const n = parseThicknessInput(raw);
  return n === null ? "" : n.toFixed(2).replace(".", ",");
}

export interface AvrHistoryTermInput {
  thicknessMm: number | string | null | undefined;
  date: string | null | undefined;
  origin?: AvrPointHistoryOrigin;
  sourceReference?: string;
}

const CIVIL = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Validação da janela HISTÓRICO DO PONTO. Cada bloco só é validado quando
 * preenchido (salvar parcial é permitido); a cronologia é obrigatória:
 * LONGO PRAZO < CURTO PRAZO < ATUAL. Sem curto prazo, o longo prazo compara
 * com a data atual.
 */
export function validatePointHistoryInput(input: { shortTerm?: AvrHistoryTermInput | null; longTerm?: AvrHistoryTermInput | null }, currentDate: string): string[] {
  const errors: string[] = [];
  const shortT = input.shortTerm ? parseThicknessInput(input.shortTerm.thicknessMm) : null;
  const shortD = input.shortTerm ? (input.shortTerm.date || "").trim() : "";
  const longT = input.longTerm ? parseThicknessInput(input.longTerm.thicknessMm) : null;
  const longD = input.longTerm ? (input.longTerm.date || "").trim() : "";
  if (input.shortTerm) {
    if (shortT === null || shortT <= 0) errors.push(AVR_SHORT_THICKNESS_INVALID);
    if (!CIVIL.test(shortD)) errors.push("Informe a data da medição de curto prazo.");
    else if (!currentDate || shortD >= currentDate) errors.push(AVR_SHORT_DATE_INVALID);
  }
  if (input.longTerm) {
    if (longT === null || longT <= 0) errors.push(AVR_LONG_THICKNESS_INVALID);
    if (!CIVIL.test(longD)) errors.push("Informe a data da medição de longo prazo.");
    else if (input.shortTerm && CIVIL.test(shortD)) {
      if (longD >= shortD) errors.push(AVR_LONG_BEFORE_SHORT_INVALID);
    } else if (!currentDate || longD >= currentDate) errors.push(AVR_LONG_DATE_INVALID);
  }
  return errors;
}

/** Leituras anteriores do MESMO ponto correlacionado (TAG + ponto + componente + localização), da mais recente para a mais antiga. */
export function previousReadingsOf(readings: readonly AvrThicknessReading[]): AvrThicknessReading[] {
  const sorted = [...readings].sort((a, b) => a.date.localeCompare(b.date));
  return sorted.slice(0, -1).reverse();
}

export function referenceLabelOf(reading: Pick<AvrThicknessReading, "reportNumber" | "pointId">): string {
  return `${reading.reportNumber} / ${reading.pointId}`;
}

/**
 * Sugestões a partir dos relatórios anteriores correlacionados: CURTO PRAZO
 * = a medição imediatamente anterior; LONGO PRAZO = a mais antiga, quando
 * distinta. Mostradas com a fonte — nunca gravadas sozinhas.
 */
export function suggestedPointHistory(readings: readonly AvrThicknessReading[]): { shortTerm: AvrEffectiveReference | null; longTerm: AvrEffectiveReference | null; candidates: AvrThicknessReading[] } {
  const previous = previousReadingsOf(readings);
  const toRef = (r: AvrThicknessReading): AvrEffectiveReference => ({ thicknessMm: r.thicknessMm, date: r.date, origin: "SOURCE_REPORT", reference: referenceLabelOf(r) });
  const shortTerm = previous[0] ? toRef(previous[0]) : null;
  const oldest = previous.length >= 2 ? previous[previous.length - 1]! : null;
  return { shortTerm, longTerm: oldest ? toRef(oldest) : null, candidates: previous };
}

/**
 * Referências efetivas de um ponto: o histórico informado no AVR tem
 * precedência; sem ele, os relatórios anteriores correlacionados; sem
 * nenhum, nulo — nunca uma taxa fictícia.
 */
export function effectiveReferencesOf(readings: readonly AvrThicknessReading[], manual: AvrPointHistory | undefined): { shortTerm: AvrEffectiveReference | null; longTerm: AvrEffectiveReference | null } {
  const suggested = suggestedPointHistory(readings);
  const fromManual = (ref: AvrHistoryReference | null | undefined): AvrEffectiveReference | null =>
    ref ? { thicknessMm: ref.thicknessMm, date: ref.date, origin: ref.origin, reference: ref.sourceReference || AVR_POINT_HISTORY_ORIGIN_LABELS[ref.origin] } : null;
  return {
    shortTerm: fromManual(manual?.shortTerm) ?? suggested.shortTerm,
    longTerm: fromManual(manual?.longTerm) ?? suggested.longTerm
  };
}

export type AvrPointHistoryState = "PENDENTE" | "PARCIAL" | "COMPLETO";

export function pointHistoryState(refs: { shortTerm: AvrEffectiveReference | null; longTerm: AvrEffectiveReference | null }): AvrPointHistoryState {
  if (refs.shortTerm && refs.longTerm) return "COMPLETO";
  if (refs.shortTerm || refs.longTerm) return "PARCIAL";
  return "PENDENTE";
}

export interface AvrComplementaryData {
  /** "aaaa-mm-dd" */
  evaluationDate: string;
  criterionNorm: string;
  /** Adotada quando o relatório de origem não registra a mínima do ponto. */
  minimumThicknessAdoptedMm: number | null;
  minimumThicknessOrigin: string;
  assumptions: string;
  proposedInspectionInterval: string;
  observations: string;
}

export interface AvrReport {
  id: string;
  clientId: ClientId;
  avrNumber: string;
  clientReportNumber: string;
  revision: string;
  status: AvrReportStatus;

  // Contexto
  projectId: string | null;
  unitAreaId: string | null;
  equipmentId: EquipmentId | null;
  clientDisplayName: string;
  projectContract: string;
  unitArea: string;
  equipmentTag: string;
  equipmentTypeLabel: string;
  material: string;
  fluid: string;
  osWorkOrderId: string | null;
  osNumber: string;
  osSubWorkOrderId: string | null;
  subOsNumber: string;

  // Fontes e dados importados (somente leitura no AVR)
  selectedSources: AvrSourceReport[];
  readings: AvrThicknessReading[];
  /** Espessura/Data Anterior por ponto, informadas no AVR — independentes, com origem. */
  pointHistories: AvrPointHistory[];

  complementary: AvrComplementaryData;

  // Cálculo — motor API 570
  rateBasis: Api570RateBasis;
  rateBasisJustification: string;
  components: AvrComponentAnalysis[];
  calculatedAt: string | null;
  /** Fontes mudaram depois do último cálculo: exige RECALCULAR antes de validar. */
  calculationOutdated: boolean;

  // Análise e decisão — do Engenheiro, nunca do sistema
  analysis: string;
  conclusion: { status: string; observation: string };
  recommendationIds: string[];

  croqui: { image: string; fileName: string; sourceReportNumber: string | null } | null;

  signatories: AvrSignatory[];
  issueDate: string;
  history: AvrHistoryEntry[];
  frozenSnapshot: AvrDocumentModel | null;

  /** Ciclo atual de análise do PLH — nulo enquanto nunca foi enviada. */
  plhReview: AvrPlhReview | null;
  /** Engenheiro responsável pela elaboração (quem enviou ao PLH) — não é o PLH. */
  engineerResponsible: { userId: string; professionalId: ProfessionalId | null; name: string } | null;

  signedAt: string | null;
  signedByUserId: string | null;
  signedByProfessionalId: ProfessionalId | null;

  /** REPROVAR AVR (Painel de Engenharia) — preservada, nunca excluída. */
  rejection?: AvrRejection | null;
  /** FINALIZAR RELATÓRIO — snapshot final + bloqueio de edição ordinária. */
  finalization?: AvrFinalization | null;

  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/** Linha do documento — já formatada (0,00 / dd/mm/aaaa). Preview e PDF consomem o MESMO modelo. */
export interface AvrDocumentComponentRow {
  component: string;
  location: string;
  shortTermThickness: string;
  shortTermDate: string;
  shortTermOrigin: string;
  longTermThickness: string;
  longTermDate: string;
  longTermOrigin: string;
  currentReport: string;
  currentDate: string;
  currentThickness: string;
  minimumThickness: string;
  periodYears: string;
  longTermRate: string;
  shortTermRate: string;
  adoptedRate: string;
  remainingLife: string;
  criticality: AvrCriticality;
  criticalityLabel: string;
  situationLabel: string;
}

export interface AvrDocumentModel {
  /** Visual do STUDIO congelado com o modelo. */
  visual?: DocumentVisual | null;
  avrNumber: string;
  clientReportNumber: string;
  revision: string;
  status: AvrReportStatus;
  /** Rótulo em português do status — o PDF/preview mostra "Aguardando Análise do PLH", nunca o enum. */
  statusLabel: string;
  /** Participação do PLH na análise, quando aprovada (item 33 do fluxo): nome, data e hash. */
  plhReview: { plhName: string; approvedAt: string; approvalHash: string } | null;
  issueDate: string;
  evaluationDate: string;
  qrPayload: string;
  header: {
    client: string;
    equipmentTag: string;
    equipmentType: string;
    unitArea: string;
    osNumber: string;
    subOsNumber: string;
    material: string;
    fluid: string;
  };
  summary: { total: number; low: number; moderate: number; high: number; critical: number; noData: number };
  components: AvrDocumentComponentRow[];
  criticalPoint: { component: string; location: string; remainingLife: string; adoptedRate: string; currentThickness: string } | null;
  references: Array<{ kind: string; reportNumber: string; revision: string; testDate: string; approved: boolean }>;
  corrosionRate: { method: string; basisLabel: string; justification: string; period: string; reportsUsed: string };
  complementary: AvrComplementaryData;
  analysis: string;
  conclusion: { status: string; worstCondition: string; observation: string };
  recommendations: Array<{ zr: string; scope: string }>;
  croqui: { image: string; fileName: string; sourceReportNumber: string | null } | null;
  signatories: Array<{ role: AvrSignatoryRole; roleLabel: string; name: string; functionTitle: string; credential: string; signatureImage: string | null; date: string }>;
}

/** Classificação do modelo oficial (avr-domain.ts) aplicada ao resultado do motor. */
export function classifyComponent(analysis: Pick<AvrComponentAnalysis, "readings" | "minimumThicknessMm" | "api570" | "shortTerm" | "longTerm">): { situation: AvrSituation; criticality: AvrCriticality } {
  const current = analysis.readings.length > 0 ? analysis.readings[analysis.readings.length - 1]! : null;
  if (!current) return { situation: "INSUFFICIENT_HISTORY", criticality: "UNCLASSIFIED" };
  if (analysis.minimumThicknessMm === null) return { situation: "NO_MINIMUM", criticality: "UNCLASSIFIED" };
  if (current.thicknessMm <= analysis.minimumThicknessMm) return { situation: "BELOW_MINIMUM", criticality: "CRITICAL" };
  // Sem medição anterior (do relatório correlacionado ou do histórico informado no AVR) não há taxa.
  if ((!analysis.shortTerm && !analysis.longTerm) || !analysis.api570) return { situation: "INSUFFICIENT_HISTORY", criticality: "UNCLASSIFIED" };
  const rate = analysis.api570.adoptedRateMmPerYear;
  if (rate === null) return { situation: "INSUFFICIENT_HISTORY", criticality: "UNCLASSIFIED" };
  if (rate <= 0) return { situation: "NO_LOSS", criticality: "UNCLASSIFIED" };
  const life = analysis.api570.remainingLifeYears;
  return { situation: "OK", criticality: determineAvrCriticality(current.thicknessMm, analysis.minimumThicknessMm, life) };
}

export function summarizeCriticality(components: readonly AvrComponentAnalysis[]): AvrDocumentModel["summary"] {
  const summary = { total: components.length, low: 0, moderate: 0, high: 0, critical: 0, noData: 0 };
  for (const c of components) {
    if (c.criticality === "LOW") summary.low++;
    else if (c.criticality === "MODERATE") summary.moderate++;
    else if (c.criticality === "HIGH") summary.high++;
    else if (c.criticality === "CRITICAL") summary.critical++;
    else summary.noData++;
  }
  return summary;
}

/** Resumo matemático (item 31): menor espessura, maior taxa, menor vida — nunca uma conclusão. */
export function findCriticalPoint(components: readonly AvrComponentAnalysis[]): AvrComponentAnalysis | null {
  const rank: Record<AvrCriticality, number> = { CRITICAL: 0, HIGH: 1, MODERATE: 2, LOW: 3, UNCLASSIFIED: 4 };
  const calculated = components.filter(c => c.criticality !== "UNCLASSIFIED");
  if (calculated.length === 0) return null;
  return [...calculated].sort((a, b) => {
    const r = rank[a.criticality] - rank[b.criticality];
    if (r !== 0) return r;
    const la = a.api570?.remainingLifeYears ?? Number.POSITIVE_INFINITY;
    const lb = b.api570?.remainingLifeYears ?? Number.POSITIVE_INFINITY;
    return la - lb;
  })[0]!;
}

/** Pior classificação presente — o "CONDIÇÃO MAIS CRÍTICA" do modelo. */
export function worstCriticality(components: readonly AvrComponentAnalysis[]): AvrCriticality {
  const order: AvrCriticality[] = ["CRITICAL", "HIGH", "MODERATE", "LOW", "UNCLASSIFIED"];
  for (const level of order) if (components.some(c => c.criticality === level)) return level;
  return "UNCLASSIFIED";
}

/** Padrão brasileiro: 5,80 / 0,25 / 8,40. */
export function formatNumberBR(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value.toFixed(decimals).replace(".", ",");
}

export function formatCivilDate(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((value ?? "").trim());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (value ?? "");
}

export interface AvrValidationResult {
  valid: boolean;
  errors: string[];
}

/** "Validar": completude — cada mensagem nomeia o que falta. */
/* ══════════════════════════════════════════════════════════════════════
   STATUS GERAL DA AVR — juízo TÉCNICO, distinto do status documental
   ══════════════════════════════════════════════════════════════════════ */

/**
 * O que a avaliação concluiu sobre o equipamento.
 *
 * NÃO SUBSTITUI `AvrReportStatus`, que descreve onde o documento está no
 * fluxo (rascunho, aguardando PLH, aprovada…). Uma AVR pode estar
 * documentalmente APROVADA pelo PLH e concluir tecnicamente REPROVADA: são
 * perguntas diferentes — "o parecer está aprovado?" e "o equipamento está
 * apto?" — e confundi-las esconderia um equipamento reprovado atrás de um
 * documento aprovado.
 *
 * Era texto livre, com placeholder "Ex.: APTO PARA OPERAÇÃO / REQUER
 * AVALIAÇÃO DE ENGENHARIA": cada engenheiro escrevia de um jeito e nada podia
 * ser filtrado, contado nem comparado entre relatórios.
 */
export type AvrGeneralStatus = "APROVADA" | "REPROVADA" | "CRITERIO_CLIENTE";

export const AVR_GENERAL_STATUS_LABELS: Record<AvrGeneralStatus, string> = {
  APROVADA: "APROVADA",
  REPROVADA: "REPROVADA",
  CRITERIO_CLIENTE: "A CRITÉRIO DO CLIENTE"
};

export const AVR_GENERAL_STATUS_OPTIONS: ReadonlyArray<{ value: AvrGeneralStatus; label: string }> =
  (Object.keys(AVR_GENERAL_STATUS_LABELS) as AvrGeneralStatus[]).map(value => ({ value, label: AVR_GENERAL_STATUS_LABELS[value] }));

export function isAvrGeneralStatus(value: unknown): value is AvrGeneralStatus {
  return value === "APROVADA" || value === "REPROVADA" || value === "CRITERIO_CLIENTE";
}

/** Rótulo impresso. Um valor legado fora da lista é mostrado como está, nunca apagado. */
export function avrGeneralStatusLabel(value: string | null | undefined): string {
  if (!value) return "";
  return isAvrGeneralStatus(value) ? AVR_GENERAL_STATUS_LABELS[value] : String(value);
}

/**
 * Traz o texto livre antigo para os três valores — e só quando não há dúvida.
 *
 * "APROVADA", "aprovado", "A CRITÉRIO DO CLIENTE" são reconhecidos. Qualquer
 * outra frase ("REQUER AVALIAÇÃO DE ENGENHARIA") NÃO é adivinhada: fica como
 * está, visível, e o engenheiro escolhe. Traduzir uma conclusão técnica por
 * palpite seria alterar o parecer de outra pessoa.
 */
export function migrateAvrGeneralStatus(value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  if (raw === "" || isAvrGeneralStatus(raw)) return raw;

  const normalized = raw.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().trim();
  if (normalized === "APROVADA" || normalized === "APROVADO") return "APROVADA";
  if (normalized === "REPROVADA" || normalized === "REPROVADO") return "REPROVADA";
  if (normalized === "A CRITERIO DO CLIENTE" || normalized === "CRITERIO DO CLIENTE") return "CRITERIO_CLIENTE";
  return raw;
}

export function validateAvrCompleteness(report: AvrReport): AvrValidationResult {
  const errors: string[] = [];
  if (!report.equipmentTag.trim()) errors.push("Informe o Equipamento / TAG.");
  if (report.selectedSources.length === 0) errors.push("Selecione ao menos um Relatório ME ou B-Scan de origem.");
  if (report.components.length === 0 || report.calculatedAt === null) errors.push("Execute CALCULAR antes de validar.");
  if (report.calculationOutdated) errors.push("As fontes mudaram depois do último cálculo: execute RECALCULAR.");
  if (!report.complementary.evaluationDate.trim()) errors.push("Informe a Data da Avaliação.");
  if (!report.analysis.trim()) errors.push("Registre a Análise Técnica / Conclusão de Engenharia.");
  if (!report.conclusion.status.trim()) errors.push("Informe o Status Geral da AVR na Conclusão.");
  // Valor legado em texto livre não bloqueia salvar, mas não pode fechar a
  // avaliação: o Status Geral precisa ser um dos três valores oficiais.
  else if (!isAvrGeneralStatus(report.conclusion.status.trim())) {
    errors.push(`Status Geral da AVR inválido ("${report.conclusion.status.trim()}"). Selecione APROVADA, REPROVADA ou A CRITÉRIO DO CLIENTE.`);
  }
  if (!report.signatories.some(s => s.role === "ELABORADO_POR" && s.professionalId)) errors.push("Informe o Profissional que elaborou a AVR.");
  const unapproved = report.selectedSources.filter(s => !s.approved);
  if (unapproved.length > 0) errors.push(`Fonte não aprovada usada na avaliação: ${unapproved.map(s => s.reportNumber).join(", ")}.`);
  return { valid: errors.length === 0, errors };
}

export const AVR_OUTDATED_BEFORE_SUBMIT_MESSAGE = "O AVR possui dados alterados após o último cálculo. Execute CALCULAR novamente antes de enviar para análise do PLH.";

/**
 * "Enviar para análise do PLH": tudo o que a validação normal exige e mais o
 * histórico por ponto — sem espessura/data anterior não há taxa, logo não há
 * envio definitivo. As espessuras não se digitam: a "anterior" é a leitura do
 * Relatório ME/B-Scan anterior do mesmo ponto.
 */
export function validateAvrForSubmission(report: AvrReport): AvrValidationResult {
  const errors: string[] = [];
  if (!report.clientId) errors.push("Informe o Cliente.");
  if (!report.projectId) errors.push("Informe o Projeto.");
  if (!report.unitAreaId) errors.push("Informe a Unidade / Área.");
  if (!report.equipmentId) errors.push("Informe o Equipamento / TAG.");
  if (report.readings.length === 0 && report.selectedSources.length > 0) errors.push("Os relatórios de origem selecionados não possuem leituras importadas.");
  if (report.calculationOutdated) errors.push(AVR_OUTDATED_BEFORE_SUBMIT_MESSAGE);
  // O método (API 570) usa as duas referências: curto e longo prazo. Cada falta é nomeada por ponto.
  for (const c of report.components) {
    if (!c.shortTerm) errors.push(`${c.pointId}: informe a medição de curto prazo.`);
    if (!c.longTerm) errors.push(`${c.pointId}: informe a medição de longo prazo.`);
    if (c.shortTerm && c.longTerm && c.api570 === null) errors.push(`${c.pointId}: taxa de corrosão não calculada. Execute CALCULAR.`);
  }
  const base = validateAvrCompleteness(report).errors.filter(e => !/execute RECALCULAR/.test(e));
  for (const e of base) if (!errors.includes(e)) errors.push(e);
  return { valid: errors.length === 0, errors };
}

/** Relatório gravado por versão anterior abre inteiro: coleções ausentes viram vazias. */
export function normalizeAvrReport(report: AvrReport): AvrReport {
  const r = report as any;
  if (r.rejection === undefined) r.rejection = null;
  if (r.finalization === undefined) r.finalization = null;
  for (const key of ["selectedSources", "readings", "components", "recommendationIds", "signatories", "history", "pointHistories"]) {
    if (!Array.isArray(r[key])) r[key] = [];
  }
  // Histórico gravado pela versão de referência única ("Espessura Anterior") vira CURTO PRAZO.
  r.pointHistories = r.pointHistories.map((h: any) => {
    if (h.shortTerm !== undefined || h.longTerm !== undefined) return { key: h.key, pointId: h.pointId, shortTerm: h.shortTerm ?? null, longTerm: h.longTerm ?? null };
    const legacy = h.previousThicknessMm !== undefined && h.previousThicknessMm !== null
      ? { thicknessMm: Number(h.previousThicknessMm), date: h.previousDate || "", origin: h.origin || "MANUAL", sourceReference: h.sourceReference || "", updatedAt: h.updatedAt || "", updatedBy: h.updatedBy || "" }
      : null;
    return { key: h.key, pointId: h.pointId, shortTerm: legacy, longTerm: null };
  });
  for (const c of r.components) {
    if (c.shortTerm === undefined) {
      c.shortTerm = c.previousThicknessMm !== undefined && c.previousThicknessMm !== null
        ? { thicknessMm: c.previousThicknessMm, date: c.previousDate || "", origin: c.previousOrigin || "SOURCE_REPORT", reference: c.previousReference || "" }
        : (c.readings && c.readings.length > 1 ? { thicknessMm: c.readings[c.readings.length - 2].thicknessMm, date: c.readings[c.readings.length - 2].date, origin: "SOURCE_REPORT", reference: `${c.readings[c.readings.length - 2].reportNumber} / ${c.pointId}` } : null);
    }
    if (c.longTerm === undefined) {
      c.longTerm = c.readings && c.readings.length > 2 ? { thicknessMm: c.readings[0].thicknessMm, date: c.readings[0].date, origin: "SOURCE_REPORT", reference: `${c.readings[0].reportNumber} / ${c.pointId}` } : null;
    }
    delete c.previousThicknessMm; delete c.previousDate; delete c.previousOrigin; delete c.previousReference;
  }
  if (!r.complementary) {
    r.complementary = { evaluationDate: "", criterionNorm: "", minimumThicknessAdoptedMm: null, minimumThicknessOrigin: "", assumptions: "", proposedInspectionInterval: "", observations: "" };
  }
  if (!r.conclusion) r.conclusion = { status: "", observation: "" };
  // Texto livre gravado antes dos três valores oficiais: só é convertido
  // quando corresponde exatamente a um deles (ver migrateAvrGeneralStatus).
  r.conclusion.status = migrateAvrGeneralStatus(r.conclusion.status);
  if (r.croqui === undefined) r.croqui = null;
  if (r.frozenSnapshot === undefined) r.frozenSnapshot = null;
  if (r.plhReview === undefined) r.plhReview = null;
  if (r.engineerResponsible === undefined) r.engineerResponsible = null;
  if (typeof r.calculationOutdated !== "boolean") r.calculationOutdated = false;
  if (!r.rateBasis) r.rateBasis = "HIGHEST";
  if (typeof r.rateBasisJustification !== "string") r.rateBasisJustification = "";
  return report;
}
